import { useReadContracts } from 'wagmi'
import { ZERO_ADDR } from '@/lib/contracts'
import { POLL_SLOW } from '@/lib/utils'


/// Identify a price feed by PROBING it, never by matching its address against a table.
///
/// A hardcoded address→provider map is the drift-prone version: it is correct only until the next
/// redeploy or the next chain, and it fails silently by mislabelling rather than loudly by
/// erroring. Every feed shape this protocol can point at carries a distinguishing getter, so
/// asking the contract what it is stays correct with no maintenance:
///
///   `description()`  Chainlink's own aggregator interface. Returns e.g. "ETH / USD", which is
///                    better than any label this app could invent, so it is shown verbatim.
///   `base()`         `ComposedPriceFeed` — an asset price times a rate feed.
///   `wstETH()`       `WstEthRateFeed` — the live Lido exchange rate.
///   `vault()`        `ERC4626RateFeed` — assets-per-share of an external 4626 vault.
///
/// Anything that answers none of them is reported as an unrecognised feed rather than guessed at.
/// The address is always shown regardless, because that is the part someone can actually verify.
const PROBES = [
  { fn: 'description', abi: [{ type: 'function', name: 'description', stateMutability: 'view', inputs: [], outputs: [{ type: 'string' }] }] },
  { fn: 'base',        abi: [{ type: 'function', name: 'base',        stateMutability: 'view', inputs: [], outputs: [{ type: 'address' }] }] },
  { fn: 'wstETH',      abi: [{ type: 'function', name: 'wstETH',      stateMutability: 'view', inputs: [], outputs: [{ type: 'address' }] }] },
  { fn: 'vault',       abi: [{ type: 'function', name: 'vault',       stateMutability: 'view', inputs: [], outputs: [{ type: 'address' }] }] },
]

export function usePriceFeedInfo(feed) {
  const enabled = !!feed && feed !== ZERO_ADDR

  const { data } = useReadContracts({
    query: { enabled, refetchInterval: POLL_SLOW },
    contracts: PROBES.map((p) => ({ address: feed, abi: p.abi, functionName: p.fn })),
  })

  if (!enabled) return { provider: null, description: null, known: false }
  if (!data) return { provider: null, description: null, known: false, loading: true }

  const [desc, base, wsteth, vault] = data

  // Ordered most-specific first. A ComposedPriceFeed wraps a Chainlink feed but does not itself
  // answer `description`, so there is no ambiguity in practice - the order is defensive.
  if (base?.status === 'success') return { provider: 'Composed feed', description: 'An asset price multiplied by a live exchange rate', known: true }
  if (wsteth?.status === 'success') return { provider: 'Lido rate', description: 'Read live from the wstETH contract', known: true }
  if (vault?.status === 'success') return { provider: 'ERC-4626 rate', description: 'Assets per share, read from the vault', known: true }
  if (desc?.status === 'success' && desc.result) return { provider: 'Chainlink', description: String(desc.result), known: true }

  return { provider: null, description: null, known: false }
}
